import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import attendikLogo from "../assets/attendik-logo.png";
import { api, isMockMode } from "../api/client.js";
import { isApiError } from "../api/errors.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { useAuth } from "../store/auth.js";

/**
 * Login screen — centred card, minimal aesthetic. On success the JWT is
 * stored and the operator lands on the dashboard (build plan P2-T4).
 */
export function LoginPage(): JSX.Element {
  const { token, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.login(email, password);
      login(result.access_token, result.refresh_token);
      navigate("/dashboard");
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        setError("Invalid email or password.");
      } else if (isApiError(err) && err.code === "NETWORK") {
        setError("Could not connect. Check your connection.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/40 p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img
            src={attendikLogo}
            alt="Attendik"
            className="mx-auto h-12 w-auto"
          />
          <p className="mt-3 text-sm text-muted-foreground">
            Sign in to the back-office portal
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error ? (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
        {isMockMode() ? (
          <p className="text-center text-xs text-muted-foreground">
            Mock mode — try{" "}
            <code className="font-mono">admin@attendik.local</code> or{" "}
            <code className="font-mono">admin@acme.local</code> /{" "}
            <code className="font-mono">Admin123!</code>
          </p>
        ) : null}
      </div>
    </div>
  );
}
